import { useEffect, useState } from "react";
import { Box, Button, Flex, IconButton, Text, useColorModeValue } from "@chakra-ui/react";
import { IoClose, IoRefreshOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import { applyPwaUpdate, PWA_UPDATE_EVENT } from "../registerServiceWorker";
import { MOBILE_BOTTOM_NAV_TOTAL_HEIGHT } from "../constants/mobileApp";

export default function PwaUpdateToast({ showMobileDock }) {
  const { t } = useTranslation();
  const [updateReady, setUpdateReady] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const shellBg = useColorModeValue("rgba(255,255,255,0.96)", "rgba(18,18,20,0.96)");
  const shellBorder = useColorModeValue("rgba(15, 23, 42, 0.1)", "rgba(255, 255, 255, 0.08)");
  const titleColor = useColorModeValue("gray.900", "white");
  const mutedColor = useColorModeValue("gray.600", "whiteAlpha.700");
  const closeHoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.150");

  useEffect(() => {
    const handleUpdate = () => setUpdateReady(true);
    window.addEventListener(PWA_UPDATE_EVENT, handleUpdate);
    return () => window.removeEventListener(PWA_UPDATE_EVENT, handleUpdate);
  }, []);

  if (!updateReady || dismissed) return null;

  const bottomOffset = showMobileDock
    ? `calc(${MOBILE_BOTTOM_NAV_TOTAL_HEIGHT} + 12px)`
    : "calc(12px + env(safe-area-inset-bottom, 0px))";

  return (
    <Box
      position="fixed"
      left={3}
      right={3}
      bottom={bottomOffset}
      zIndex={1200}
      borderRadius="2xl"
      border="1px solid"
      borderColor={shellBorder}
      bg={shellBg}
      backdropFilter="blur(20px) saturate(180%)"
      sx={{ WebkitBackdropFilter: "blur(20px) saturate(180%)" }}
      boxShadow="0 12px 40px rgba(0,0,0,0.35)"
      px={4}
      py={3}
    >
      <Flex align="center" justify="space-between" gap={3}>
        <Box minW={0}>
          <Text fontSize="sm" fontWeight="semibold" color={titleColor} noOfLines={1}>
            {t("pwa.updateTitle")}
          </Text>
          <Text fontSize="xs" color={mutedColor} mt={0.5} noOfLines={2}>
            {t("pwa.updateSubtitle")}
          </Text>
        </Box>
        <Flex align="center" gap={1} flexShrink={0}>
          <Button
            size="sm"
            h="34px"
            px={4}
            leftIcon={<IoRefreshOutline size={16} />}
            bgGradient="linear(to-r, brand.300, brand.500)"
            color="gray.900"
            _hover={{ bgGradient: "linear(to-r, brand.400, brand.600)" }}
            onClick={applyPwaUpdate}
          >
            {t("pwa.refresh")}
          </Button>
          <IconButton
            icon={<IoClose size={16} />}
            aria-label={t("pwa.dismiss")}
            variant="ghost"
            size="sm"
            h="34px"
            minW="34px"
            borderRadius="md"
            color={titleColor}
            opacity={0.65}
            _hover={{ bg: closeHoverBg, opacity: 1 }}
            onClick={() => setDismissed(true)}
          />
        </Flex>
      </Flex>
    </Box>
  );
}
